/**
 * Display helpers.
 *
 * Money is NOT here. It comes from `formatIQD` in packages/shared/src/money.ts and from
 * nowhere else — the field app, the API's `total_display` and this console must agree
 * character for character, and a second formatter is how they stop agreeing.
 */

import type { JobState } from "@fanni/shared/job-state";
import type { Priority, UploadState, PaymentMethod, InvoiceStatus, MaterialUnit } from "./types.js";

/**
 * Dates are built by hand rather than by `Intl.DateTimeFormat("ar-IQ")`, and that is not
 * not-invented-here.
 *
 * Intl's Arabic formats embed RIGHT-TO-LEFT MARKs (U+200F) inside their separators — the
 * output for 2 May 2026 is literally "02‏/05‏/2026". Those marks are invisible and
 * they are also instructions: dropped into the LTR-isolated run that money and timestamps
 * live in (`.num`, styles.css), they reorder the segments and the date renders "022026/05/".
 * A date that silently rearranges itself on an evidence screen is worse than no date.
 *
 * So: big-endian `YYYY/MM/DD`, Western digits (matching formatIQD), no bidi controls, and it
 * sorts correctly read down a column. Local time — the manager is in Baghdad and so is the
 * technician he is asking about.
 */
const p2 = (n: number): string => String(n).padStart(2, "0");

const ymd = (d: Date): string => `${d.getFullYear()}/${p2(d.getMonth() + 1)}/${p2(d.getDate())}`;
const hm = (d: Date): string => `${p2(d.getHours())}:${p2(d.getMinutes())}`;

export const fmtDate = (iso: string | null | undefined): string =>
  iso ? ymd(new Date(iso)) : "—";

export const fmtTime = (iso: string | null | undefined): string =>
  iso ? hm(new Date(iso)) : "—";

export const fmtDateTime = (iso: string | null | undefined): string => {
  if (!iso) return "—";
  const d = new Date(iso);
  return `${ymd(d)} ${hm(d)}`;
};

/** ISO date for <input type="date"> and the board's `?date=` param. */
export const isoDay = (d: Date): string =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

export const today = (): string => isoDay(new Date());

export function fmtDuration(minutes: number | null): string {
  if (minutes === null) return "—";
  if (minutes < 60) return `${Math.round(minutes)} د`;
  const h = Math.floor(minutes / 60);
  const m = Math.round(minutes % 60);
  return m === 0 ? `${h} س` : `${h} س ${m} د`;
}

/** Whole days elapsed since an instant. Null when there is no instant. */
export function daysSince(iso: string | null, now = Date.now()): number | null {
  if (!iso) return null;
  return Math.floor((now - new Date(iso).getTime()) / 86_400_000);
}

/**
 * Whole days past an invoice's due date. Positive = late by that many days; 0 or negative =
 * not late yet; null = no due date was ever set, which is NOT the same as "not due" and is
 * rendered as "—" rather than as a zero.
 *
 * This replaces an `AGED_DAYS = 30` proxy that measured age since ISSUE, because
 * GET /money/invoices did not return `due_at`. It does now (routes/money.ts), so the console
 * asks the real question. The proxy was not merely imprecise: on net-14 terms it reported
 * "0 فاتورة لم تُحصّل" while 920,500 د.ع was genuinely late, because the late invoices were
 * only 5–15 days old and the proxy wanted 30.
 */
export function daysOverdue(dueAt: string | null | undefined, now = Date.now()): number | null {
  if (!dueAt) return null;
  return Math.floor((now - new Date(dueAt).getTime()) / 86_400_000);
}

/** Past the date the customer agreed to, and still not fully paid. */
export const isOverdue = (
  inv: { dueAt: string | null; outstanding_fils: number; status: string },
  now = Date.now(),
): boolean =>
  inv.outstanding_fils > 0 &&
  (inv.status === "issued" || inv.status === "partial") &&
  inv.dueAt !== null &&
  new Date(inv.dueAt).getTime() < now;

export const PRIORITY_AR: Record<Priority, string> = {
  low: "منخفضة",
  normal: "عادية",
  high: "عالية",
  emergency: "طارئة",
};

export const UPLOAD_STATE_AR: Record<UploadState, string> = {
  pending: "بانتظار الرفع",
  uploading: "قيد الرفع",
  stored: "محفوظة",
  failed: "فشل الرفع",
};

export const PAYMENT_METHOD_AR: Record<PaymentMethod, string> = {
  cash: "نقداً",
  transfer: "حوالة",
  cheque: "صك",
};

export const INVOICE_STATUS_AR: Record<InvoiceStatus, string> = {
  draft: "مسودة",
  issued: "صادرة",
  partial: "مدفوعة جزئياً",
  paid: "مدفوعة",
  void: "ملغاة",
};

export const UNIT_AR: Record<MaterialUnit, string> = {
  piece: "قطعة",
  kg: "كغم",
  gram: "غم",
  meter: "متر",
  litre: "لتر",
  hour: "ساعة",
};

export const MEDIA_KIND_AR: Record<string, string> = {
  before: "قبل",
  after: "بعد",
  issue: "العطل",
  part: "القطعة",
  meter: "القراءة",
  signature: "التوقيع",
};

export const ACCOUNT_KIND_AR: Record<string, string> = {
  asset: "أصول",
  liability: "التزامات",
  equity: "حقوق ملكية",
  revenue: "إيرادات",
  expense: "مصروفات",
};

/**
 * Status → colour. The one saturated colour on a job card, because status is what the manager
 * is scanning a hundred cards for. The Arabic label always comes from JOB_STATE_AR; this maps
 * only the colour.
 */
export const STATUS_TONE: Record<JobState, "neutral" | "info" | "active" | "good" | "warn" | "bad"> = {
  draft: "neutral",
  scheduled: "neutral",
  assigned: "info",
  en_route: "active",
  on_site: "active",
  in_progress: "active",
  paused: "warn",
  completed: "good",
  invoiced: "good",
  closed: "neutral",
  cancelled: "bad",
};
